import React, { useEffect, useMemo, useState } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebaseConfig';

interface DispatchRow {
  date?: string;
  company?: string;
  route?: string;
  vehicleNumber?: string;
  carNumber?: string;
  cargo?: string;
  contact?: string;
  driverName?: string;
  paymentAmount?: number | string;
  billingAmount?: number | string;
  [key: string]: any;
}

function getTodayStr(): string {
  const today = new Date();
  return today.toISOString().slice(0, 10);
}

function getThreeDaysAgoStr(): string {
  const threeDaysAgo = new Date();
  threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
  return threeDaysAgo.toISOString().slice(0, 10);
}

function getSevenDaysAgoStr(): string {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
}

function getDateList(start: string, end: string): string[] {
  const result: string[] = [];
  let cur = new Date(start);
  const endDate = new Date(end);
  while (cur <= endDate) {
    result.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  return result;
}

const MobileDispatchList: React.FC = () => {
  const [startDate, setStartDate] = useState<string>(getTodayStr());
  const [endDate, setEndDate] = useState<string>(getTodayStr());
  const [currentSort, setCurrentSort] = useState<'asc' | 'desc'>('desc');
  const [rows, setRows] = useState<DispatchRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());

  // 초기 날짜 한정: 오늘로 셋업, 입력 가능 범위는 3일 전 ~ 오늘
  useEffect(() => {
    const today = getTodayStr();
    setStartDate(today);
    setEndDate(today);
  }, []);

  const limitedStart = useMemo(() => getSevenDaysAgoStr(), []);
  // 미래 날짜 무제한 허용을 위해 매우 큰 날짜를 최대값으로 사용
  const limitedEnd = useMemo(() => '9999-12-31', []);

  const fetchData = async () => {
    // 사용자가 지정한 범위만 조회 (시작은 7일 전까지, 미래 날짜는 제한 없음)
    const aWeekAgo = getSevenDaysAgoStr();
    const s = (startDate || aWeekAgo);
    const e = (endDate || aWeekAgo);
    // 범위 보정: 시작은 7일 이전이면 7일 전으로, 종료는 제한 없음. start<=end 보장
    const sanitizedStart = s < aWeekAgo ? aWeekAgo : s;
    const sanitizedEnd = e;
    const finalStart = sanitizedStart <= sanitizedEnd ? sanitizedStart : sanitizedEnd;
    const finalEnd = sanitizedStart <= sanitizedEnd ? sanitizedEnd : sanitizedStart;
    const dateList = getDateList(finalStart, finalEnd);
    setLoading(true);
    setError('');
    setRows([]);
    try {
      const allRows: DispatchRow[] = [];
      for (const d of dateList) {
        const res = await fetch(`/api/dispatch-reports/${d}/rows`);
        if (!res.ok) throw new Error('데이터 조회 실패');
        const data: DispatchRow[] = await res.json();
        data.forEach(r => { r.date = d; });
        allRows.push(...data);
      }
      setRows(allRows);
    } catch (e: any) {
      setError(e?.message || '데이터를 가져오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 최초 로드는 무조건 오늘 날짜만
    const fetchToday = async () => {
      const today = getTodayStr();
      setLoading(true);
      setError('');
      setRows([]);
      try {
        const res = await fetch(`/api/dispatch-reports/${today}/rows`);
        if (!res.ok) throw new Error('데이터 조회 실패');
        const data: DispatchRow[] = await res.json();
        data.forEach(r => { r.date = today; });
        setRows(data);
      } catch (e: any) {
        setError(e?.message || '데이터를 가져오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };
    fetchToday();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredSorted = useMemo(() => {
    const filtered = rows
      .filter(item => {
        // company가 있는데 차량번호와 운송구간이 모두 없으면 제외
        if (!item.company) return true;
        return !!(item.vehicleNumber || item.carNumber || item.route);
      });
    const sorted = filtered.slice().sort((a, b) => {
      const da = a.date || '';
      const db = b.date || '';
      const cmp = currentSort === 'desc' ? db.localeCompare(da) : da.localeCompare(db);
      if (cmp !== 0) return cmp;
      return (a.company || '').toLowerCase().localeCompare((b.company || '').toLowerCase());
    });
    return sorted;
  }, [rows, currentSort]);

  const copySingle = (item: DispatchRow) => {
    const text = `거래처상호: ${item.company || ''}\n날짜: ${item.date || ''}\n운행구간: ${item.route || ''}\n연락처: ${item.contact || ''}\n이름: ${item.driverName || ''}`;
    navigator.clipboard.writeText(text);
    alert('복사되었습니다!');
  };

  const copyMulti = () => {
    if (checkedIds.size === 0) {
      alert('복사할 항목을 선택하세요.');
      return;
    }
    const arr = [...checkedIds].sort((a, b) => a - b).map(idx => filteredSorted[idx]);
    let text = '';
    arr.forEach((item, i) => {
      text += `거래처상호: ${item.company || ''}\n날짜: ${item.date || ''}\n운행구간: ${item.route || ''}\n연락처: ${item.contact || ''}\n이름: ${item.driverName || ''}`;
      if (i < arr.length - 1) text += '\n\n---\n\n';
    });
    navigator.clipboard.writeText(text);
    alert('선택한 항목이 복사되었습니다!');
    setCheckedIds(new Set());
  };

  const formatAmount = (val: any): string => {
    if (val === null || val === undefined || val === '') return '';
    const num = typeof val === 'number' ? val : Number(String(val).replace(/[^\d.-]/g, ''));
    if (isNaN(num)) return '';
    return num.toLocaleString();
  };

  return (
    <div style={{ maxWidth: 520, margin: '0 auto', padding: 12, height: '100vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch', touchAction: 'pan-y', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '12px 0 16px 0' }}>
        <h2 style={{ color: '#388e3c', margin: 0 }}>배차 목록</h2>
        <button
          onClick={async () => {
            try {
              await signOut(auth);
            } catch {}
            try {
              localStorage.clear();
              sessionStorage.clear();
            } catch {}
            window.location.href = '/';
          }}
          style={{
            border: '1px solid #ddd',
            background: '#fff',
            color: '#666',
            borderRadius: 6,
            padding: '6px 10px',
            fontSize: 14,
            cursor: 'pointer'
          }}
        >
          로그아웃
        </button>
      </div>

      {/* 날짜 범위 (UI는 선택 가능, 실제 조회는 3일 고정) */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input
          type="date"
          value={startDate}
          min={limitedStart}
          max={limitedEnd}
          onChange={e => setStartDate(e.target.value)}
          style={{ flex: 1, height: 44, fontSize: 16, padding: '8px 10px', border: '1px solid #ddd', borderRadius: 4 }}
        />
        <span style={{ alignSelf: 'center' }}>~</span>
        <input
          type="date"
          value={endDate}
          min={limitedStart}
          max={limitedEnd}
          onChange={e => setEndDate(e.target.value)}
          style={{ flex: 1, height: 44, fontSize: 16, padding: '8px 10px', border: '1px solid #ddd', borderRadius: 4 }}
        />
      </div>

      <button onClick={fetchData} style={{ width: '100%', height: 44, fontSize: 18, fontWeight: 700, background: '#43a047', color: '#fff', borderRadius: 6, border: 'none' }}>조회</button>

      {/* 정렬 버튼 */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, margin: '10px 0' }}>
        <button
          onClick={() => setCurrentSort('desc')}
          className={currentSort === 'desc' ? 'active' : ''}
          style={{ background: currentSort === 'desc' ? '#43a047' : '#f5f5f5', color: currentSort === 'desc' ? '#fff' : '#222', border: '1px solid #43a047', fontSize: 16, fontWeight: 700, borderRadius: 6, padding: '8px 16px' }}
        >
          날짜 내림차순
        </button>
        <button
          onClick={() => setCurrentSort('asc')}
          className={currentSort === 'asc' ? 'active' : ''}
          style={{ background: currentSort === 'asc' ? '#43a047' : '#f5f5f5', color: currentSort === 'asc' ? '#fff' : '#222', border: '1px solid #43a047', fontSize: 16, fontWeight: 700, borderRadius: 6, padding: '8px 16px' }}
        >
          날짜 오름차순
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 20, textAlign: 'center', color: '#888' }}>불러오는 중...</div>
      ) : error ? (
        <div style={{ padding: 20, textAlign: 'center', color: '#d32f2f' }}>{error}</div>
      ) : filteredSorted.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: '#888' }}>데이터가 없습니다.</div>
      ) : (
        <div className="dispatch-list">
          {filteredSorted.map((item, idx) => (
            <div key={idx} className="dispatch-list-row" style={{ borderBottom: '1px solid #eee', padding: '14px 0 10px 0', display: 'flex', alignItems: 'flex-start' }} onClick={() => setSelectedIdx(idx)}>
              <div className="dispatch-list-row-checkbox" style={{ marginRight: 10, marginTop: 4 }} onClick={e => e.stopPropagation()}>
                <input type="checkbox" checked={checkedIds.has(idx)} onChange={e => {
                  const next = new Set(checkedIds);
                  if (e.target.checked) next.add(idx); else next.delete(idx);
                  setCheckedIds(next);
                }} />
              </div>
              <div className="dispatch-list-row-content" style={{ flex: 1 }}>
                <div className="dispatch-list-row-main" style={{ fontWeight: 700, fontSize: 16, color: '#222', marginBottom: 2, display: 'flex', gap: 8 }}>
                  <span className="date" style={{ minWidth: 90, color: '#388e3c' }}>{item.date || ''}</span>
                  <span>{item.company || ''}</span>
                </div>
                <div className="dispatch-list-row-sub" style={{ fontSize: 15, color: '#444', marginBottom: 2, wordBreak: 'break-all' }}>{item.route || ''}</div>
                <div className="dispatch-list-row-bottom" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                  <span className="car-number" style={{ color: '#555' }}>{item.vehicleNumber || item.carNumber || (item as any)['차량번호'] || ''}</span>
                  <span className="cargo" style={{ color: '#888' }}>{item.cargo || ''}</span>
                  <a className="phone-link" style={{ color: '#2196F3', textDecoration: 'none' }} href={`tel:${item.contact || ''}`}>{item.contact || ''}</a>
                  <button className="copy-btn" style={{ background: '#4CAF50', color: '#fff', border: 'none', padding: '5px 10px', borderRadius: 4, fontSize: 13, minWidth: 38, marginLeft: 8 }} onClick={() => copySingle(item)}>복사</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {checkedIds.size > 0 && (
        <button onClick={copyMulti} style={{ position: 'fixed', left: '50%', bottom: 18, transform: 'translateX(-50%)', width: '90vw', maxWidth: 500, height: 48, fontSize: 18, fontWeight: 700, background: '#2196F3', color: '#fff', borderRadius: 8, border: 'none', boxShadow: '0 2px 8px rgba(33,150,243,0.15)' }}>다중 복사</button>
      )}

      {/* 상세 모달 */}
      {selectedIdx !== null && filteredSorted[selectedIdx] && (
        <div className="modal-bg" onClick={() => setSelectedIdx(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, maxWidth: '90vw', width: 350, padding: '24px 18px 18px 18px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)', position: 'relative' }}>
            <button onClick={() => setSelectedIdx(null)} className="modal-close" style={{ position: 'absolute', top: 10, right: 14, fontSize: 22, color: '#888', background: 'none', border: 'none', cursor: 'pointer' }}>&times;</button>
            <div className="modal-title" style={{ fontSize: 20, fontWeight: 700, marginBottom: 12, textAlign: 'center' }}>배차 상세 내역</div>
            {(() => { const d = filteredSorted[selectedIdx]; return (
              <>
                <div className="modal-row" style={{ marginBottom: 8, fontSize: 16, display: 'flex' }}>
                  <span className="modal-label" style={{ minWidth: 90, color: '#388e3c', fontWeight: 700 }}>거래처상호</span>
                  <span className="modal-value" style={{ color: '#222', wordBreak: 'break-all' }}>{d.company || ''}</span>
                </div>
                <div className="modal-row" style={{ marginBottom: 8, fontSize: 16, display: 'flex' }}>
                  <span className="modal-label" style={{ minWidth: 90, color: '#388e3c', fontWeight: 700 }}>날짜</span>
                  <span className="modal-value" style={{ color: '#222', wordBreak: 'break-all' }}>{d.date || ''}</span>
                </div>
                <div className="modal-row" style={{ marginBottom: 8, fontSize: 16, display: 'flex' }}>
                  <span className="modal-label" style={{ minWidth: 90, color: '#388e3c', fontWeight: 700 }}>차량번호</span>
                  <span className="modal-value" style={{ color: '#222', wordBreak: 'break-all' }}>{d.vehicleNumber || d.carNumber || (d as any)['차량번호'] || ''}</span>
                </div>
                <div className="modal-row" style={{ marginBottom: 8, fontSize: 16, display: 'flex' }}>
                  <span className="modal-label" style={{ minWidth: 90, color: '#388e3c', fontWeight: 700 }}>운행구간</span>
                  <span className="modal-value" style={{ color: '#222', wordBreak: 'break-all' }}>{d.route || ''}</span>
                </div>
                <div className="modal-row" style={{ marginBottom: 8, fontSize: 16, display: 'flex' }}>
                  <span className="modal-label" style={{ minWidth: 90, color: '#388e3c', fontWeight: 700 }}>차종</span>
                  <span className="modal-value" style={{ color: '#222', wordBreak: 'break-all' }}>{d.cargo || ''}</span>
                </div>
                <div className="modal-row" style={{ marginBottom: 8, fontSize: 16, display: 'flex' }}>
                  <span className="modal-label" style={{ minWidth: 90, color: '#388e3c', fontWeight: 700 }}>청구운송비</span>
                  <span className="modal-value" style={{ color: '#222', wordBreak: 'break-all' }}>{formatAmount(d.billingAmount)}</span>
                </div>
                <div className="modal-row" style={{ marginBottom: 8, fontSize: 16, display: 'flex' }}>
                  <span className="modal-label" style={{ minWidth: 90, color: '#388e3c', fontWeight: 700 }}>지급운송비</span>
                  <span className="modal-value" style={{ color: '#222', wordBreak: 'break-all' }}>{formatAmount(d.paymentAmount)}</span>
                </div>
                <div className="modal-row" style={{ marginBottom: 8, fontSize: 16, display: 'flex' }}>
                  <span className="modal-label" style={{ minWidth: 90, color: '#388e3c', fontWeight: 700 }}>연락처</span>
                  <span className="modal-value" style={{ color: '#222', wordBreak: 'break-all' }}>
                    <a href={`tel:${d.contact || ''}`} style={{ color: '#2196F3', textDecoration: 'none' }}>{d.contact || ''}</a>
                  </span>
                </div>
                <div className="modal-row" style={{ marginBottom: 8, fontSize: 16, display: 'flex' }}>
                  <span className="modal-label" style={{ minWidth: 90, color: '#388e3c', fontWeight: 700 }}>이름</span>
                  <span className="modal-value" style={{ color: '#222', wordBreak: 'break-all' }}>{d.driverName || ''}</span>
                </div>
              </>
            ); })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileDispatchList;


